import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { PredictorModelActions } from '../../state/predictor-model/predictor-model.actions';
import { PredictorModelState } from '../../state/predictor-model/predictor-model.state';
import { StudyArea } from '../interfaces/study-area';

/**
 * Resolver function to handle the selection and dispatching of study areas.
 *
 * This function is used to resolve the study area based on the route parameter 'id'.
 * It interacts with the store to either select a specific study area or reset to defaults.
 *
 * @param route - The activated route snapshot containing route parameters.
 * @returns An observable that resolves to a boolean indicating the success of the operation or null.
 */
export const StudyAreaResolver: ResolveFn<Observable<boolean> | null> = (
  route: ActivatedRouteSnapshot
) => {
  const store: Store = inject(Store);
  const id: string | null = route.paramMap.get('id');

  const selectStudyArea: (studyAreas: StudyArea[], id: string | null) => Observable<boolean> = (studyAreas, id) => {
    if (!id){
      return store.dispatch(new PredictorModelActions.ResetToDefaults()).pipe(map(() => true));
    }
    const selectedStudyArea: StudyArea | undefined = studyAreas.find(area => area.id === id);
    if (selectedStudyArea) {
      return store.dispatch(
        new PredictorModelActions.SetSelectedStudyArea(id, selectedStudyArea.studyAreaName,selectedStudyArea.licenseNumber)
      ).pipe(map(() => true));
    } else {
      return store.dispatch(new PredictorModelActions.ResetToDefaults()).pipe(map(() => true));
    }
  };

  const studyAreas: StudyArea[] = store.selectSnapshot(PredictorModelState.studyAreas);

  if (!studyAreas || studyAreas.length === 0) {
    return store.dispatch(new PredictorModelActions.GetStudyAreas()).pipe(
      mergeMap(() => {
        const updatedStudyAreas: StudyArea[] = store.selectSnapshot(PredictorModelState.studyAreas);
        return selectStudyArea(updatedStudyAreas, id);
      })
    );
  }

  return selectStudyArea(studyAreas, id);
};
